import { CallBase, RecordType, PopRecords, OpenRecord, ParamsType } from 'rcx_adapter';
import { SessionData } from 'rcx_adapter/src/typing';
import { parseJsonIfValid } from 'rcx_adapter/src/util';

import { BaseModule } from './BaseModule';
import CreateEntityRecord from './CreateEntityRecord';
import { type ServiceNowRecordItem } from '../adapter/ServiceNowAdapter.interface';
import { LOGGING_TYPE, tableNameMap } from '../constants';
import { ModuleBaseOption } from '../interfaces/module';

const TYPE = {
    VOICE: 'voice',
    DIGITAL: 'digital',
    LEAD: 'lead',
};

enum STAGE {
    RINGING = 'RINGING',
    ANSWERED = 'ANSWERED',
}

interface PopRecordData {
    type: string;
    uii: string;
    engage: CallBase;
    matchRecords?: ServiceNowRecordItem[];
    stage?: STAGE;
}

class PopRecordModule extends BaseModule<ModuleBaseOption> {
    private _dataMap = new Map<string, PopRecordData>();
    private popIdMap: Map<string, string> = new Map();

    public start(): void {
        if (this.getIsInit()) {
            this.registerToRX();
        }
    }
    public registerToRX() {
        this.mRCXAdapter!.onNewCall(this.onNewCall.bind(this));
        this.mRCXAdapter!.onCallIn(this.onCallAnswer.bind(this));
        this.mRCXAdapter!.onEndCall(this.clearEventMap.bind(this));
        this.mRCXAdapter!.onCallRejected(this.clearEventMap.bind(this));
        this.mRCXAdapter!.onOpenRecord(this.onOpenRecord.bind(this));
        this.mRCXAdapter!.onPopRecords(this.onPopRecords.bind(this));
    }

    private parseUiiForCallBase(call: CallBase) {
        return call.leadId || call.lead?.leadId || call.uii || '';
    }

    private onNewCall(call: CallBase) {
        const uii = this.parseUiiForCallBase(call);
        this._dataMap.set(uii, {
            type: TYPE.VOICE,
            uii: call.uii,
            engage: call,
            matchRecords: [],
            stage: STAGE.RINGING,
        });

        this.openNewIncidentPage(call);
    }

    private onCallAnswer(call: CallBase) {
        const uii = this.parseUiiForCallBase(call);
        if (!uii) {
            return;
        }

        let data = this._dataMap.get(uii);
        if (!data) {
            data = {
                uii,
                type: TYPE.VOICE,
                engage: call,
            };
        }
        this._dataMap.set(uii, {
            ...data,
            stage: STAGE.ANSWERED,
        });
        this.tryToPopRecord(uii);
        this.openNewIncidentPage(call);
    }

    private clearEventMap(data: any) {
        const uii = this.parseUiiForCallBase(data);
        this._dataMap.delete(uii);
        this.popIdMap.delete(uii);
    }
    private async onOpenRecord(data: OpenRecord) {
        const entity = tableNameMap[data.type as keyof typeof tableNameMap];
        if (!entity) {
            return;
        }

        return this.mServiceNowAdapter!.openFrameOpenServiceNowFormBySysId(data.params, entity);
    }

    private async onPopRecords(data: PopRecords) {
        const { params, uii = '', matchType, type } = data;
        if (!params) {
            return false;
        }
        if (type && !this.popIdMap.has(uii)) {
            this.mServiceNowAdapter!.openFrameOpenServiceNowFormBySysId(params, type);
            if (matchType !== TYPE.LEAD) {
                this.popIdMap.set(uii, uii);
            }
            return;
        }
    }

    public onMatchRecords(matchRecords: ServiceNowRecordItem[], engageInfo: CallBase) {
        const uii = this.parseUiiForCallBase(engageInfo);
        if (!uii) {
            return;
        }
        let data = this._dataMap.get(uii);

        if (!data && this.isPreviewLead(engageInfo)) {
            data = {
                type: TYPE.LEAD,
                uii,
                engage: engageInfo,
                matchRecords,
            };
            this._dataMap.set(uii, data);
        }

        if (!data || matchRecords.length > 1) {
            return;
        }

        data.matchRecords = matchRecords;
        this.tryToPopRecord(uii);
    }

    private tryToPopRecord(uii: string) {
        const data = this._dataMap.get(uii);
        if (!data) {
            return;
        }

        const matchRecords = data.matchRecords;
        const isSingleMatch = matchRecords?.length === 1;
        const noMatch = matchRecords?.length === 0;

        if (isSingleMatch) {
            const shouldPopRecordWhenMatch = this.isAllowPop(data);
            if (shouldPopRecordWhenMatch) {
                const currentUii = uii || new Date().getTime().toString();
                const { sys_id = '', sys_class_name = '' } = matchRecords[0];

                this.onPopRecords({
                    uii: currentUii,
                    params: sys_id,
                    type: sys_class_name,
                    matchType: data.type,
                });
            }
        } else if (noMatch) {
            if (data.engage && (this.isPreviewLead(data.engage) || this.isActivePredictiveOutbound(data.engage))) {
                CreateEntityRecord.onPopNewEntity({ type: RecordType.CONTACT, call: data.engage });
            }

            if (this.isActivePredictiveOutbound(data.engage)) {
                this.popIdMap.set(uii, uii);
            }
        }
    }

    private isAllowPop(data: PopRecordData) {
        const { engage, stage } = data;
        if (this.isPreviewLead(engage) || this.isActivePredictiveOutbound(engage)) {
            return true;
        }

        if (engage?.callType !== 'INBOUND') {
            return false;
        }

        if (engage.isManualAnswerCall) {
            return stage === STAGE.ANSWERED;
        }
        return stage === STAGE.ANSWERED || stage === STAGE.RINGING;
    }

    private isPreviewLead(call: CallBase) {
        const isLead = call.leadId || call?.lead?.leadId;
        const isNotCalled = !call.callState;
        return Boolean(isLead) && isNotCalled;
    }

    private isActivePredictiveOutbound(call: CallBase) {
        const uii = this.parseUiiForCallBase(call);
        const stage = this._dataMap.get(uii)?.stage;
        return call.isPredictiveOutbound && (stage === STAGE.RINGING || stage === STAGE.ANSWERED);
    }

    private async openNewIncidentPage(call: CallBase) {
        if (!this.shouldOpenNewIncidentPage(call)) {
            return;
        }

        const adapter = this.mServiceNowAdapter!;
        const logAdapter = adapter.getLogAdapter();
        const callId = call.uii;

        const localData = await logAdapter.getStorageLog(callId, ParamsType.CALL);
        if (!localData) {
            await logAdapter.initStorageLog({
                params: call,
                paramsType: ParamsType.CALL,
                id: callId,
            });
        }
        await logAdapter.saveLogToSN({
            callId,
            paramsType: ParamsType.CALL,
        });
    }

    private shouldOpenNewIncidentPage(call: CallBase = {} as CallBase): boolean {
        if (this.mServiceNowAdapter!.getCallLoggingType() !== LOGGING_TYPE.INCIDENT) {
            return false;
        }

        const uii = this.parseUiiForCallBase(call);
        const sessionData: SessionData = call?.sessionData ? parseJsonIfValid(call.sessionData) : {};

        const callData = this._dataMap.get(uii);
        const isCallAnswered = call.isManualAnswerCall
            ? callData?.stage === STAGE.ANSWERED
            : callData?.stage === STAGE.RINGING;

        if (!isCallAnswered) {
            return false;
        }

        const matchingResult = sessionData?.cx?.crm?.matchingResult;
        const isMatchingResultEmpty = !matchingResult?.length;
        if (isMatchingResultEmpty) {
            return true;
        }

        const incidentResult = matchingResult.find(
            (item) => item?.crmEntityType?.toLowerCase() === RecordType.INCIDENT.toLowerCase()
        );

        const validMatchedIds = incidentResult?.crmMatchedIds?.filter(Boolean) || [];
        if (incidentResult && validMatchedIds.length === 0) {
            return true;
        }

        return false;
    }
}

export default new PopRecordModule();
